import { hiddenStarts, type DayEvent, type EndReason } from '../stores/events'
import type { ChildProfile } from './types'
import { forProfile } from './programs'
import { minutesBefore, totalMinutes } from './engine'
import { rate, type Rating } from './day-stats'

/**
 * Окна бодрствования по факту — для аналитики (решение заказчика 07.10.2026).
 *
 * Точка — окно, а не день: утреннее короткое и вечернее длинное видны по отдельности.
 * Длительность — от «Проснулся» до ответа «да, уснул», какой бы ни была программа:
 * фазу завершили раньше — значит, цикл и правда кончился раньше.
 *
 * Окна без ответа «уснул» в аналитику не идут вовсе, ни длинные, ни короткие. Без ответа
 * неизвестно, что это было — перегул или забытое приложение, — а график, где половина
 * точек по факту, а половина по плану, врёт о тренде. Это же довод отвечать на вопрос.
 *
 * Начало — нажатое «Проснулся». Если таймер включили не сразу, а на поздней фазе (перешли
 * к ней в первые минуты окна), первые фазы считаем прошедшими по плану и начало
 * отодвигаем назад — так же делает движок. Такое начало расчётное, а не наблюдённое,
 * и помечается `approx`: иначе цифра выглядит точнее, чем она есть.
 */

const МИН = 60_000
const DAY = 24 * 3600 * 1000

/** Переход к фазе в первые минуты окна — это «включили таймер поздно», а не «фаза кончилась раньше». */
export const ПОЗДНИЙ_СТАРТ_МИН = 3

export interface WakeWindow {
  /** Нажатое «Проснулся» — ключ окна в истории и журнале. */
  at: number
  /** Начало бодрствования: нажатое или расчётное, если таймер включили на поздней фазе. */
  start: number
  /** Начало расчётное, не наблюдённое. */
  approx: boolean
  /** Чем кончилось; `null` — конца в журнале нет: окно идёт или его так и не закрыли. */
  end: number | null
  reason: EndReason | null
  /** Ответили «да, уснул»: только такие окна идут в аналитику. */
  confirmed: boolean
  /** Длительность бодрствования в минутах — только у подтверждённых. */
  minutes: number | null
  /** Окно вписали руками в календаре, а не отсчитал таймер. */
  manual: boolean
  /** Время поправили руками поверх окна таймера (правка времени, 20.10.2026). */
  fixed: boolean
  /** Комментарий к бодрствованию. Где уснул и как — это уже про сон, он рядом в списке. */
  note?: string
  /** Окно по возрасту из программы на тот день, минуты. */
  norm: readonly [number, number]
  /** План таймера на тот день, минуты: по нему считается окно без ответа «уснул». */
  plan: number
  rating: Rating
}

/**
 * Окна по порядку: начала из истории и из записей, вписанных руками; фазы и концы — из
 * журнала; норма по возрасту на тот день.
 *
 * У окна, вписанного руками, начала в истории нет — нажатия «Проснулся» не было, — поэтому
 * его начало берётся прямо из записи (просьба 12.10.2026).
 */
export function wakeWindows(events: DayEvent[], windows: number[], profile: ChildProfile): WakeWindow[] {
  const поОкнам = new Map<number, DayEvent[]>()
  const руками = new Set<number>()
  // Начала, заменённые правкой: прячутся только в истории — ручная запись, вернувшая окно
  // на то же начало, всё равно видна, потому что сама его и несёт
  const скрытые = hiddenStarts(events)
  for (const e of events) {
    if (e.deleted === true) continue
    if (e.kind !== 'phase' && e.kind !== 'window_end') continue
    if (e.kind === 'window_end' && e.manual === true) руками.add(e.window)
    const список = поОкнам.get(e.window)
    if (список === undefined) поОкнам.set(e.window, [e])
    else список.push(e)
  }
  return [...new Set([...windows.filter((at) => !скрытые.has(at)), ...руками])]
    .sort((a, b) => a - b)
    .map((at) => {
      const свои = поОкнам.get(at) ?? []
      const program = forProfile(profile, at)
      // Конец у окна один. Если их несколько (старое устройство дописало свой), верен
      // самый ранний: поздний превращал окно в многочасовое (жалоба 09.10.2026)
      const конец = свои.filter((e) => e.kind === 'window_end').sort((a, b) => a.at - b.at)[0] ?? null
      // Поздний старт: самый поздний переход к фазе в первые минуты окна
      const поздний = свои
        .filter((e) => e.kind === 'phase' && (e.index ?? 0) > 0 && e.at - at <= ПОЗДНИЙ_СТАРТ_МИН * МИН)
        .sort((a, b) => b.at - a.at)[0]
      const start = поздний === undefined ? at : Math.min(at, поздний.at - minutesBefore(program, поздний.index ?? 0) * МИН)
      // Первая запись окна — не нулевая фаза: таймер включили сразу с нужной фазы,
      // то есть начало посчитано по плану, а не нажато (случай 09.10.2026)
      const перваяФаза = свои.filter((e) => e.kind === 'phase').sort((a, b) => a.at - b.at)[0]
      const сФазы = перваяФаза !== undefined && (перваяФаза.index ?? 0) > 0
      const вручную = конец?.manual === true
      const confirmed = конец?.reason === 'asleep'
      const minutes = confirmed && конец !== null ? Math.max(0, Math.round((конец.at - start) / МИН)) : null
      return {
        at,
        start,
        // Вписанное руками время наблюдённое: его вспоминал человек, а не считал движок
        approx: вручную ? false : start !== at || сФазы,
        manual: вручную,
        fixed: вручную && typeof конец?.replaces === 'number',
        ...(конец?.wakeNote === undefined ? {} : { note: конец.wakeNote }),
        end: конец?.at ?? null,
        reason: конец?.reason ?? null,
        confirmed,
        minutes,
        norm: program.windowMin,
        plan: totalMinutes(program),
        rating: minutes === null ? 'none' : rate(minutes, program.windowMin),
      }
    })
}

/**
 * Конец окна, который показываем: ответ «уснул», «нет» или сброс — момент ответа; без
 * ответа — по плану (авто-закрытие через два часа после плана концом не считается, как в
 * листе дня); идущее окно — до текущего момента.
 */
export function shownEnd(w: WakeWindow, now: number): number {
  if (w.end !== null && (w.confirmed || w.reason === 'awake' || w.reason === 'reset')) return w.end
  return Math.min(now, w.start + w.plan * МИН)
}

/**
 * Когда закрылось окно, начатое в [start]; null — живой записи о конце нет.
 *
 * Удалённые записи не в счёт: правка окна помечает прежний конец удалённым, а снятие
 * окна вовсе пишет удалённую запись-метку. Считать их концом нельзя — именно из-за этого
 * ответ «уснул» заводил в истории второе начало, и в ленте появлялось окно-двойник
 * «без ответа» рядом с настоящим (жалоба заказчика 22.10.2026).
 */
export function windowClosedAt(events: DayEvent[], start: number): number | null {
  const конец = events
    .filter((e) => e.deleted !== true && e.kind === 'window_end' && e.window === start)
    .sort((a, b) => a.at - b.at)[0]
  return конец?.at ?? null
}

/** Дольше — это уже описка во времени. */
export const САМОЕ_ДЛИННОЕ_ОКНО_Ч = 12

export type WindowProblem = { kind: 'bad_range' | 'too_long' | 'ahead' } | { kind: 'overlap'; window: WakeWindow } | null

/** Дольше этого промежуток — уже не сон, а забытое приложение (как в сводке дня). */
export const САМЫЙ_ДОЛГИЙ_СОН_Ч = 30

export type SleepProblem = { kind: 'bad_range' | 'too_long' | 'ahead' | 'before_wake' | 'after_next' } | null

/**
 * Что не так со временем сна. У сна своей записи нет: его начало — это конец предыдущего
 * бодрствования, а конец — следующее «Проснулся». Поэтому границы нельзя занести за
 * соседей: уснуть раньше, чем проснулся, и проснуться позже, чем уснул в следующий раз.
 */
export function sleepProblem(from: number, to: number, prevWakeStart: number, nextSleepStart: number | null, now: number): SleepProblem {
  if (to <= from) return { kind: 'bad_range' }
  if (from <= prevWakeStart) return { kind: 'before_wake' }
  if (to > now) return { kind: 'ahead' }
  if (nextSleepStart !== null && to >= nextSleepStart) return { kind: 'after_next' }
  if (to - from > САМЫЙ_ДОЛГИЙ_СОН_Ч * 3600_000) return { kind: 'too_long' }
  return null
}

/**
 * Что не так со временем окна «с–по» рядом с уже записанными [others]; null — всё в порядке.
 * Одна проверка на вписывание и на правку: правка не должна налезать на соседей, а само
 * правленое окно в [others] не передают.
 */
export function windowProblem(start: number, end: number, others: WakeWindow[], now: number): WindowProblem {
  if (end <= start) return { kind: 'bad_range' }
  if (end - start > САМОЕ_ДЛИННОЕ_ОКНО_Ч * 3600_000) return { kind: 'too_long' }
  if (end > now) return { kind: 'ahead' }
  const window = others.find((w) => start < (w.end ?? w.start) && end > w.start)
  return window === undefined ? null : { kind: 'overlap', window }
}

export type WakeState = 'ok' | 'low' | 'high' | 'empty'

export interface WakeSummary {
  state: WakeState
  /** Средняя длительность подтверждённого окна за последние семь дней, минуты. */
  average: number | null
  /** То же за семь дней до них: для фразы «выросло с … до …». */
  previous: number | null
  /** Окно по возрасту сейчас и подпись программы: «75–90» и «2–3 месяца». */
  norm: readonly [number, number]
  ageLabel: string
  /** Окон без ответа «уснул» за последние семь дней: их на графике нет, и об этом стоит сказать. */
  unconfirmed: number
}

function среднее(list: WakeWindow[]): number | null {
  const минуты = list.map((w) => w.minutes).filter((m): m is number => m !== null)
  if (минуты.length === 0) return null
  return Math.round(минуты.reduce((s, m) => s + m, 0) / минуты.length)
}

/** Сводка для карточки состояния: средняя за неделю против окна по возрасту. Факт, не диагноз. */
export function wakeSummary(all: WakeWindow[], profile: ChildProfile, now: number): WakeSummary {
  const program = forProfile(profile, now)
  const неделя = all.filter((w) => w.start > now - 7 * DAY && w.start <= now)
  const прошлая = all.filter((w) => w.start > now - 14 * DAY && w.start <= now - 7 * DAY)
  const average = среднее(неделя.filter((w) => w.confirmed))
  const общее = {
    average,
    previous: среднее(прошлая.filter((w) => w.confirmed)),
    norm: program.windowMin,
    ageLabel: program.ageLabel,
    unconfirmed: неделя.filter((w) => !w.confirmed && w.end !== null).length,
  }
  if (average === null) return { state: 'empty', ...общее }
  const r = rate(average, program.windowMin)
  return { state: r === 'none' ? 'empty' : r, ...общее }
}
